import { map, some, sortBy, trim } from "lodash";
import { AICC_LANGUAGE_STATUS, AiccLanguageStatus } from "constants/aiccLanguage";
import {
  AiccCanDoStatementResponse,
  AiccCanDoStatementUploadPreviewResponse,
  AiccSttModelResponse,
  AiccCanDoStatementVersionsResponse,
  AiccDefaultScenarioResponse,
  AiccDefaultScenarioVersionsResponse,
  AiccLanguageNameResponse,
  AiccLessonEntryResponse,
  AiccLessonEntryRowResponse,
  AiccAssetUploadIssueResponse,
  AiccLessonEntryUploadPreviewResponse,
  AiccLessonEntryVersionsResponse,
  AiccLessonInformationResponse,
  AiccLessonInformationUploadPreviewResponse,
  AiccLessonInformationVersionsResponse,
  AiccLessonLevelResponse,
  AiccLessonReviewScriptResponse,
  AiccLessonReviewScriptUploadPreviewResponse,
  AiccLessonReviewScriptVersionsResponse,
  AiccReviewScriptResponse,
  AiccScenarioCategoryResponse,
  AiccScenarioCategoryVersionsResponse,
  AiccSttSettingVersionsResponse,
  AiccTtsSettingVersionsResponse,
  AiccTtsVoiceResponse,
  AiccUiElementTranslationResponse,
  AiccUiElementTranslationVersionsResponse,
} from "../dto/response/aiccLearningAssets";
import {
  AiccCanDoStatementReplaceRequest,
  AiccCanDoStatementRequest,
  AiccDefaultScenarioCreateRequest,
  AiccDefaultScenarioUpdateRequest,
  AiccLessonEntryReplaceRequest,
  AiccLessonEntryRequest,
  AiccLessonEntryUpdateRequest,
  AiccLessonInformationReplaceRequest,
  AiccLessonInformationRequest,
  AiccLessonReviewScriptReplaceRequest,
  AiccScenarioCategoryCreateRequest,
  AiccScenarioCategoryUpdateRequest,
  AiccSttSettingUpdateRequest,
  AiccTtsPreviewRequest,
  AiccTtsSettingUpdateRequest,
  AiccUiElementTranslationUpdateRequest,
} from "../dto/request/aiccLearningAssets";
import {
  AICC_STT_MODE,
  AiccAssetUploadIssue,
  AiccAssetUploadPreview,
  AiccAssetVersionGroup,
  AiccAssetVersions,
  AiccCanDoStatement,
  AiccCanDoStatementPayload,
  AiccCanDoStatementUploadPreview,
  AiccCanDoStatementUploadRow,
  AiccDefaultScenario,
  AiccDefaultScenarioPayload,
  AiccLanguageName,
  AiccLessonEntry,
  AiccLessonEntryPayload,
  AiccLessonEntryUploadPreview,
  AiccLessonInformation,
  AiccLessonInformationPayload,
  AiccLessonInformationUploadPreview,
  AiccLessonLevel,
  AiccLessonReviewScript,
  AiccLessonReviewScriptUploadPreview,
  AiccReviewScript,
  AiccScenarioCategory,
  AiccSttMode,
  AiccSttOption,
  AiccScenarioCategoryPayload,
  AICC_STT_ROLE,
  AiccSttSettingPayload,
  AiccSttSettingRow,
  AICC_TTS_MODEL,
  AICC_TTS_PROVIDER,
  AiccTtsPreviewPayload,
  AiccTtsVoiceRow,
  AiccUiElementTranslationRow,
  AiccVoiceGender,
} from "../model/aiccLearningAssets";

interface AssetUploadPreviewResponse<R> {
  valid: boolean;
  errors?: AiccAssetUploadIssueResponse[];
  rows?: R[];
}

function transformAssetVersions<G extends { status: AiccLanguageStatus }, R, T>(
  response: { versions?: G[] },
  getItems: (group: G) => R[] | undefined,
  transformItem: (item: R) => T,
  getGroupExtras?: (group: G) => Partial<AiccAssetVersionGroup<T>>
): AiccAssetVersions<T> {
  return {
    versions: sortBy(
      map(response.versions ?? [], (group) => ({
        status: group.status,
        items: map(getItems(group) ?? [], transformItem),
        ...getGroupExtras?.(group),
      })),
      ({ status }) => (status === AICC_LANGUAGE_STATUS.public ? 1 : 0)
    ),
  };
}

function transformAiccDefaultScenario(response: AiccDefaultScenarioResponse): AiccDefaultScenario {
  return {
    id: response.id,
    title: response.title ?? "",
    learnerContextDescription: response.description ?? "",
    aiContextDescription: response.context_description ?? "",
    voiceGender: response.voice_gender as AiccVoiceGender,
  };
}

export function transformAiccDefaultScenarioVersions(response: AiccDefaultScenarioVersionsResponse) {
  return transformAssetVersions(response, ({ scenarios }) => scenarios, transformAiccDefaultScenario);
}

export function toAiccDefaultScenarioCreateRequest(
  payload: AiccDefaultScenarioPayload
): AiccDefaultScenarioCreateRequest {
  return {
    title: payload.title,
    description: payload.learnerContextDescription,
    context_description: payload.aiContextDescription,
    voice_gender: payload.voiceGender,
  };
}

export function toAiccDefaultScenarioUpdateRequest(
  payload: AiccDefaultScenarioPayload
): AiccDefaultScenarioUpdateRequest {
  return {
    title: payload.title,
    description: payload.learnerContextDescription,
    context_description: payload.aiContextDescription,
    voice_gender: payload.voiceGender,
  };
}

function transformAiccScenarioCategory(response: AiccScenarioCategoryResponse): AiccScenarioCategory {
  return {
    id: response.id,
    name: response.name ?? "",
    description: response.description ?? "",
  };
}

export function transformAiccScenarioCategoryVersions(response: AiccScenarioCategoryVersionsResponse) {
  return transformAssetVersions(response, ({ categories }) => categories, transformAiccScenarioCategory);
}

export function toAiccScenarioCategoryCreateRequest(
  payload: AiccScenarioCategoryPayload
): AiccScenarioCategoryCreateRequest {
  return {
    name: payload.name,
    description: payload.description,
  };
}

export function toAiccScenarioCategoryUpdateRequest(
  payload: AiccScenarioCategoryPayload
): AiccScenarioCategoryUpdateRequest {
  return {
    name: payload.name,
    description: payload.description,
  };
}

export function transformAiccSttSettingVersions(response: AiccSttSettingVersionsResponse) {
  return transformAssetVersions(
    response,
    (group): AiccSttSettingRow[] => [
      { id: AICC_STT_ROLE.primary, optionKey: group.primary_stt },
      { id: AICC_STT_ROLE.fallback, optionKey: group.fallback_stt },
    ],
    (row) => row
  );
}

export function toAiccSttSettingUpdateRequest(payload: AiccSttSettingPayload): AiccSttSettingUpdateRequest {
  return {
    primary_stt: payload.primaryKey,
    fallback_stt: payload.fallbackKey,
  };
}

function transformAiccTtsVoiceRow(response: AiccTtsVoiceResponse): AiccTtsVoiceRow {
  return {
    id: response.gender,
    main: { voiceId: response.main_voice_id, baseSpeed: response.main_base_speed },
    fallback: { voiceId: response.fallback_voice_id, baseSpeed: response.fallback_base_speed },
  };
}

export function transformAiccTtsSettingVersions(
  response: AiccTtsSettingVersionsResponse
): AiccAssetVersions<AiccTtsVoiceRow> {
  return {
    // Each group's own model: a published version and the working copy can run different models. A backend
    // that sends no per-group model sends only the response-level one.
    ...transformAssetVersions(
      response,
      ({ voices }) => voices,
      transformAiccTtsVoiceRow,
      (group) => ({ ttsModel: group.tts_model ?? response.tts_model })
    ),
    ttsModelOptions: map(response.tts_model_options ?? [], (option) => ({
      id: option.id,
      displayName: option.display_name || option.id,
      supportsStreamPlay: option.supports_stream_play,
    })),
  };
}

/** The voices, plus `tts_model` only when the selection differs from the saved model. */
export function toAiccTtsSettingUpdateRequest(
  rows: AiccTtsVoiceRow[],
  selectedModel?: string,
  savedModel?: string
): AiccTtsSettingUpdateRequest {
  return {
    voices: map(rows, ({ id, main, fallback }) => ({
      gender: id,
      main_voice_id: main.voiceId,
      main_base_speed: main.baseSpeed,
      fallback_voice_id: fallback.voiceId,
      fallback_base_speed: fallback.baseSpeed,
    })),
    ...(selectedModel && selectedModel !== savedModel ? { tts_model: selectedModel } : {}),
  };
}

export function toAiccTtsPreviewRequest(languageId: number, payload: AiccTtsPreviewPayload): AiccTtsPreviewRequest {
  return {
    provider: AICC_TTS_PROVIDER,
    language_id: languageId,
    text: payload.text,
    config: { voice_id: payload.voiceId, model_id: payload.model },
    base_speed: payload.baseSpeed,
    // Without the backend's options there is no capability to read; v3 was then the only streamed model.
    streaming: payload.streaming ?? payload.model === AICC_TTS_MODEL.v3,
  };
}

function transformAiccUiElementTranslation(response: AiccUiElementTranslationResponse): AiccUiElementTranslationRow {
  return {
    id: response.element,
    translation: response.translation ?? "",
  };
}

export function transformAiccUiElementTranslationVersions(response: AiccUiElementTranslationVersionsResponse) {
  return transformAssetVersions(response, ({ translations }) => translations, transformAiccUiElementTranslation);
}

export function toAiccUiElementTranslationUpdateRequest(
  rows: AiccUiElementTranslationRow[]
): AiccUiElementTranslationUpdateRequest {
  return {
    translations: map(rows, ({ id, translation }) => ({ element: id, translation: trim(translation) })),
  };
}

export function transformAiccLanguageName(response: AiccLanguageNameResponse): AiccLanguageName {
  return {
    id: response.id,
    name: response.name,
  };
}

function transformAiccLessonLevels(levels?: AiccLessonLevelResponse[]): AiccLessonLevel[] {
  return sortBy(
    map(levels ?? [], ({ level, lessons }) => ({ level, lessons: sortBy(lessons ?? []) })),
    "level"
  );
}

function transformAiccLessonEntryRow(response: AiccLessonEntryRowResponse): AiccLessonEntryPayload {
  return {
    level: response.level,
    lesson: response.lesson,
    text: response.word ?? "",
    translation: response.translation ?? "",
    transliteration: response.transliteration ?? "",
  };
}

function transformAiccLessonEntry(response: AiccLessonEntryResponse): AiccLessonEntry {
  return {
    ...transformAiccLessonEntryRow(response),
    id: `${response.level}-${response.lesson}-${response.index}`,
    index: response.index,
  };
}

export function transformAiccLessonEntryVersions(
  response: AiccLessonEntryVersionsResponse
): AiccAssetVersions<AiccLessonEntry> {
  return {
    ...transformAssetVersions(response, ({ items }) => items, transformAiccLessonEntry),
    hasTransliteration: !!response.has_transliteration,
    filters: transformAiccLessonLevels(response.filters),
  };
}

export function toAiccLessonEntryRequest(payload: AiccLessonEntryPayload): AiccLessonEntryRequest {
  return {
    word: payload.text,
    translation: payload.translation,
    transliteration: payload.transliteration,
  };
}

export function toAiccLessonEntryUpdateRequest(payload: AiccLessonEntryPayload): AiccLessonEntryUpdateRequest {
  return {
    level: payload.level,
    lesson: payload.lesson,
    ...toAiccLessonEntryRequest(payload),
  };
}

export function toAiccLessonEntryReplaceRequest(items: AiccLessonEntryPayload[]): AiccLessonEntryReplaceRequest {
  return {
    items: map(items, toAiccLessonEntryUpdateRequest),
  };
}

function transformAssetUploadIssue({ type, message, row }: AiccAssetUploadIssueResponse): AiccAssetUploadIssue {
  return { type, message, row };
}

function transformAssetUploadPreview<R, T>(
  response: AssetUploadPreviewResponse<R>,
  transformRow: (row: R) => T
): AiccAssetUploadPreview<T> {
  return {
    isValid: response.valid,
    errors: map(response.errors ?? [], transformAssetUploadIssue),
    rows: map(response.rows ?? [], transformRow),
  };
}

export function transformAiccLessonEntryUploadPreview(
  response: AiccLessonEntryUploadPreviewResponse
): AiccLessonEntryUploadPreview {
  const preview = transformAssetUploadPreview(response, transformAiccLessonEntryRow);
  return {
    ...preview,
    hasTransliteration: some(preview.rows, ({ transliteration }) => !!trim(transliteration)),
  };
}

function transformAiccReviewScript(response: AiccReviewScriptResponse): AiccReviewScript {
  return {
    scriptIndex: response.script_index,
    title: response.title ?? "",
    sceneSetup: response.scene_setup ?? "",
    rolePlayerGender: response.role_player_gender ?? null,
    coversPhrases: response.covers_phrases ?? [],
    lines: map(response.lines ?? [], ({ speaker, cue_en: cue, text, transliteration }) => ({
      speaker,
      cue: cue ?? "",
      text: text ?? "",
      transliteration,
    })),
  };
}

function transformAiccLessonReviewScript(response: AiccLessonReviewScriptResponse): AiccLessonReviewScript {
  return {
    id: `${response.level}-${response.lesson}`,
    level: response.level,
    lesson: response.lesson,
    scripts: sortBy(map(response.scripts ?? [], transformAiccReviewScript), "scriptIndex"),
  };
}

export function transformAiccLessonReviewScriptVersions(
  response: AiccLessonReviewScriptVersionsResponse
): AiccAssetVersions<AiccLessonReviewScript> {
  return {
    ...transformAssetVersions(response, ({ lessons }) => lessons, transformAiccLessonReviewScript),
    filters: transformAiccLessonLevels(response.filters),
  };
}

export function transformAiccLessonReviewScriptUploadPreview(
  response: AiccLessonReviewScriptUploadPreviewResponse
): AiccLessonReviewScriptUploadPreview {
  return transformAssetUploadPreview(response, transformAiccLessonReviewScript);
}

function transformAiccCanDoStatement(response: AiccCanDoStatementResponse): AiccCanDoStatement {
  return {
    id: response.id,
    fluencyLevel: response.level,
    statement: response.statement ?? "",
  };
}

export function transformAiccCanDoStatementVersions(
  response: AiccCanDoStatementVersionsResponse
): AiccAssetVersions<AiccCanDoStatement> {
  const { versions } = transformAssetVersions(response, ({ statements }) => statements, transformAiccCanDoStatement);
  return {
    versions: map(versions, (group) => ({ ...group, items: sortBy(group.items, "fluencyLevel") })),
  };
}

export function toAiccCanDoStatementRequest(payload: AiccCanDoStatementPayload): AiccCanDoStatementRequest {
  return {
    level: payload.fluencyLevel,
    statement: payload.statement,
  };
}

export function toAiccCanDoStatementReplaceRequest(
  rows: AiccCanDoStatementUploadRow[]
): AiccCanDoStatementReplaceRequest {
  return {
    statements: map(rows, (row) => ({ id: row.id, ...toAiccCanDoStatementRequest(row) })),
  };
}

export function transformAiccCanDoStatementUploadPreview(
  response: AiccCanDoStatementUploadPreviewResponse
): AiccCanDoStatementUploadPreview {
  return transformAssetUploadPreview(response, ({ id, level, statement }) => ({
    id,
    fluencyLevel: level,
    statement: statement ?? "",
  }));
}

function transformAiccLessonInformation(response: AiccLessonInformationResponse): AiccLessonInformation {
  return {
    id: `${response.level}-${response.lesson}`,
    level: response.level,
    lesson: response.lesson,
    summary: response.lesson_summary ?? "",
    keyVocabulary: response.key_vocabulary ?? "",
  };
}

export function transformAiccLessonInformationVersions(
  response: AiccLessonInformationVersionsResponse
): AiccAssetVersions<AiccLessonInformation> {
  return {
    ...transformAssetVersions(response, ({ lessons }) => lessons, transformAiccLessonInformation),
    filters: transformAiccLessonLevels(response.filters),
  };
}

export function transformAiccLessonInformationUploadPreview(
  response: AiccLessonInformationUploadPreviewResponse
): AiccLessonInformationUploadPreview {
  return transformAssetUploadPreview(response, transformAiccLessonInformation);
}

export function toAiccLessonInformationRequest(payload: AiccLessonInformationPayload): AiccLessonInformationRequest {
  return {
    lesson_summary: trim(payload.summary),
    key_vocabulary: trim(payload.keyVocabulary),
  };
}

export function toAiccLessonInformationReplaceRequest(
  lessons: AiccLessonInformation[]
): AiccLessonInformationReplaceRequest {
  return {
    lessons: map(lessons, (lesson) => ({
      level: lesson.level,
      lesson: lesson.lesson,
      ...toAiccLessonInformationRequest(lesson),
    })),
  };
}

export function toAiccLessonReviewScriptReplaceRequest(
  lessons: AiccLessonReviewScript[]
): AiccLessonReviewScriptReplaceRequest {
  return {
    lessons: map(lessons, ({ level, lesson, scripts }) => ({
      level,
      lesson,
      scripts: map(scripts, ({ scriptIndex, title, sceneSetup, rolePlayerGender, coversPhrases, lines }) => ({
        script_index: scriptIndex,
        title,
        scene_setup: sceneSetup,
        role_player_gender: rolePlayerGender,
        covers_phrases: coversPhrases,
        lines: map(lines, ({ speaker, cue, text, transliteration }) => ({
          speaker,
          cue_en: cue,
          text,
          transliteration,
        })),
      })),
    })),
  };
}

/**
 * One STT engine as the screen reads it.
 *
 * The MODE is the only thing translated: the catalogue answers `"realtime"` / `"batch"` and this repo's
 * `AICC_STT_MODE` is uppercase, so the map lives here rather than in the components — an unknown value
 * keeps its uppercased self instead of throwing, since a mode this build does not know is still a group
 * heading somebody has to be able to read.
 */
export function transformAiccSttModel(response: AiccSttModelResponse): AiccSttOption {
  return {
    key: response.id,
    // The catalogue's display name, falling back to the id: a blank name would leave an operator choosing
    // between rows they cannot tell apart.
    displayName: response.display_name || response.id,
    mode: (response.mode?.toUpperCase() as AiccSttMode) ?? AICC_STT_MODE.batch,
    description: response.description ?? "",
    isDefaultPrimary: response.is_default_primary ?? false,
    isDefaultFallback: response.is_default_fallback ?? false,
    isDeprecated: response.is_deprecated ?? false,
  };
}
